import React, { FC, MouseEventHandler } from 'react';
import { Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { EXPRESSION_VARIABLES_CONSTANTS_ABBREVIATION } from '@shared/constants/calculations.constants';
import { getVariableWithNoConstantObjectBefore } from './calculations.helpers';
import { uniqueId } from 'lodash';

const MATH_REGEX = /[\s+\-/*():"'0-9?\\|]/;
const FORMULA_SPLIT_REGEX = /([!()\s])/g;
const NON_VARIABLE_WORDS = ['internal', 'calculation', '<user', 'input>'];

const getMathWithSpaceBefore = (
  variable: string,
  index: number,
  splitFormula: string[],
) =>
  `${
    index > 0 && variable !== ')' && splitFormula[index - 1] !== 'clamp'
      ? ' '
      : ''
  }${variable}`;

const getVariableWithSpaceBefore = (
  variable: string,
  index: number,
  splitFormula: string[],
) => {
  const last = splitFormula[index - 1];
  return `${index > 0 && last && !['!', '('].includes(last) ? ' ' : ''}${getVariableWithNoConstantObjectBefore(variable) ?? ''}`;
};

interface CalculationProps {
  formula: string;
  activeVariable: string | undefined;
  setActiveVariable: (variable: string | undefined) => void;
}

const Calculation: FC<CalculationProps> = ({
  formula,
  activeVariable,
  setActiveVariable,
}) => {
  const { classes, cx } = useStyles();

  const onMouseEnter: MouseEventHandler<HTMLSpanElement> = (event) => {
    const variable = event.currentTarget.dataset.variable;
    if (
      !variable ||
      MATH_REGEX.test(variable) ||
      NON_VARIABLE_WORDS.includes(variable)
    ) {
      return;
    }
    setActiveVariable(variable);
  };
  const onMouseLeave = () => setActiveVariable(undefined);

  return (
    <span style={{ marginRight: 16 }}>
      {formula
        .split(FORMULA_SPLIT_REGEX)
        .map((variable, index, splitFormula) =>
          MATH_REGEX.test(variable) || NON_VARIABLE_WORDS.includes(variable) ? (
            <span key={uniqueId()}>
              {getMathWithSpaceBefore(variable, index, splitFormula)}
            </span>
          ) : (
            <span key={uniqueId()}>
              <Typography
                className={cx(
                  classes.variable,
                  variable === activeVariable && classes.variableActive,
                  variable.startsWith(
                    EXPRESSION_VARIABLES_CONSTANTS_ABBREVIATION,
                  ) && classes.variableConstant,
                )}
                data-variable={variable}
                component="span"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              >
                {getVariableWithSpaceBefore(variable, index, splitFormula)}
              </Typography>
            </span>
          ),
        )}
    </span>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  variable: {
    lineHeight: 1,
  },
  variableActive: {
    textShadow: `0 0 1px ${theme.palette.text.primary}`,
  },
  variableConstant: {
    color: '#999',
  },
}));

export default Calculation;
